import { useMemo, useState } from "react";
import { Button } from '@/components/ui/button';

import { type Income } from '@/hooks/admin/shared/useIncome';

import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import IncomeFilters from '@/components/v2/molecules/admin/tabs/AdminIncomeTab/IncomeFilters';
import IncomeTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminIncomeTab/IncomeTabSkeleton';
import DeleteDialog from '@/components/v2/molecules/admin/tabs/shared/DeleteDialog';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import { useIncomeFilters } from '@/hooks/admin/tabs/useIncomeFilters';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import useTransactionModals from '@/hooks/admin/shared/useTransactionModals';
import { useDeleteIncome } from '@/hooks/admin/shared/useIncomeMutations';
import { Download, Plus, Trash2 } from 'lucide-react';
import IncomeFormModal from '../shared/IncomeFormModal';
import IncomeTable from './IncomeTable';
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/utils/shared/currencyUtils";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { useShiftSelect } from "@/hooks/useShiftSelect";
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const AdminIncomeTab = () => {
  const { canCreate, canEdit, canDelete, role } = useRolePermissions();
  const { toast } = useToast();

  const deleteIncome = useDeleteIncome();
  
  // Fetch organizations and contracts for filtering
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const {
    itemToEdit: incomeToEdit,
    isModalOpen: isIncomeModalOpen,
    isDeleteDialogOpen: isDeleteIncomeDialogOpen,
    handleNew: handleNewIncome,
    handleEdit: handleEditIncome,
    handleModalClose: handleIncomeModalClose,
    handleDelete: handleDeleteIncome,
    confirmDelete: confirmDeleteIncome,
    setIsDeleteDialogOpen: setIsDeleteIncomeDialogOpen,
  } = useTransactionModals<Income>({
    onDelete: async (id) => await deleteIncome.mutateAsync(id),
  });

  const {
    filteredIncomes,
    paymentTypes,
    isLoading: isLoadingIncomes,
    error: errorIncomes,
    refetch: refetchIncomes,
    // Pagination
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,
    // Filters
    searchTerm,
    selectedProperties,
    selectedOrganizations,
    selectedPaymentType,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setSearchTerm,
    setSelectedProperties,
    setSelectedOrganizations,
    setSelectedPaymentType,
    setDateFrom,
    setDateTo,
    clearFilters,
  } = useIncomeFilters();

  // Shift-select hook
  const {
    selectedItems: selectedIncomes,
    setSelectedItems: setSelectedIncomes,
    handleSelect: handleSelectIncome,
    handleSelectAll,
    clearSelection,
  } = useShiftSelect({ items: filteredIncomes });

  // Estados para modales
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

  // Funciones de selección
  const selectedIncomesTotal = useMemo(() => {
    if (selectedIncomes.length === 0) return 0;
    
    return filteredIncomes
      .filter(income => selectedIncomes.includes(income.id))
      .reduce((sum, income) => sum + (income.payout || 0), 0);
  }, [selectedIncomes, filteredIncomes]);

  // Función para descargar CSV
  const handleDownloadCSV = () => {
    // Si hay ingresos seleccionados, exportar solo esos; si no, exportar todos los filtrados
    const incomesToExport = selectedIncomes.length > 0
      ? filteredIncomes.filter(income => selectedIncomes.includes(income.id))
      : filteredIncomes;

    const csvHeaders = [
      'Reservación',
      'Nombre de Huésped',
      'Propiedad',
      'Tipo de ingreso',
      'Tipo de pago',
      'Pago bruto',
      'IVA',
      'Ret IVA',
      'Ret ISR',
      'Comisión',
      'Pago Recibido',
      'Fecha de pago',
      'Requiere Factura',
    ];

    const csvData = incomesToExport.map((income) => [
      income.booking_id || 'N/A',
      income.guest_name || 'N/A',
      income.property_short_name || 'N/A',
      income.income_type || 'N/A',
      income.payment_type || 'N/A',
      income.gross_payment?.toString() || '0',
      income.iva?.toString() || '0',
      income.ret_iva?.toString() || '0',
      income.ret_isr?.toString() || '0',
      income.comission?.toString() || '0',
      income.payout?.toString() || '0',
      income.payout_date || 'N/A',
      income.fiscal_invoice ? 'Sí' : 'No',
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `ingresos_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'CSV descargado',
      description: `Se han exportado ${incomesToExport.length} ingresos`,
    });
  };

  // Función para eliminar seleccionados
  const handleDeleteSelected = () => {
    setIsBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(
        selectedIncomes.map(incomeId => 
          deleteIncome.mutateAsync(incomeId)
        )
      );

      toast({
        title: "Éxito",
        description: `Se eliminaron ${selectedIncomes.length} ingreso(s) correctamente`,
      });

      clearSelection();
      setIsBulkDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting selected incomes:", error);
      toast({
        title: "Error",
        description: "Error al eliminar los ingresos seleccionados",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoadingIncomes) {
    return <IncomeTabSkeleton />;
  }

  if (errorIncomes) {
    return (
      <TabErrorBanner
        title="Error al cargar ingresos"
        message="No se pudieron cargar los ingresos. Por favor, intenta nuevamente."
        onRetry={() => refetchIncomes()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <AdminPageHeader title="Ingresos">
          <div className="flex items-center gap-3">
            {selectedIncomes.length > 0 && (
              <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {selectedIncomes.length} seleccionado{selectedIncomes.length > 1 ? 's' : ''} • {formatCurrency(selectedIncomesTotal)}
              </div>
            )}

            <div className="flex items-center gap-2">
              {selectedIncomes.length > 0 && canDelete && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteSelected}
                  className="flex items-center gap-2"
                  disabled={isDeleting}
                >
                  <Trash2 size={16} />
                  {isDeleting ? "Eliminando..." : `Eliminar (${selectedIncomes.length})`}
                </Button>
              )}

              <Button
                onClick={handleDownloadCSV}
                variant="outline"
                disabled={filteredIncomes.length === 0}
                className="flex items-center gap-2"
              >
                <Download size={16} />
                Descargar CSV
              </Button>

              {canCreate && filteredIncomes.length !== 0 && (
                <Button
                  onClick={handleNewIncome}
                  className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
                >
                  <Plus size={16} />
                  Crear Ingreso
                </Button>
              )}
            </div>
          </div>
        </AdminPageHeader>

        <IncomeFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          hasActiveFilters={hasActiveFilters}
          paymentTypes={paymentTypes}
          role={role}
          searchTerm={searchTerm}
          selectedProperties={selectedProperties}
          selectedOrganizations={selectedOrganizations}
          selectedPaymentType={selectedPaymentType}
          organizations={organizations}
          contracts={contracts}
          clearFilters={clearFilters}
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          setSearchTerm={setSearchTerm}
          setSelectedProperties={setSelectedProperties}
          setSelectedOrganizations={setSelectedOrganizations}
          setSelectedPaymentType={setSelectedPaymentType}
        />
      </div>

      {!filteredIncomes || filteredIncomes.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center flex flex-col items-center">
          <h3 className="text-gray-800 font-medium mb-2">No hay ingresos</h3>

          <p className="text-gray-600 text-sm mb-4">
            No se encontraron ingresos que coincidan con los filtros aplicados.
          </p>

          {canCreate && (
            <Button
              onClick={handleNewIncome}
              className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Ingreso
            </Button>
          )}
        </div>
      ) : (
        <>
          <IncomeTable
            incomes={filteredIncomes}
            onEdit={handleEditIncome}
            onDelete={handleDeleteIncome}
            canEdit={canEdit}
            canDelete={canDelete}
            selectedIncomes={selectedIncomes}
            onSelectIncome={handleSelectIncome}
            onSelectAll={handleSelectAll}
          />

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            pageSizeOptions={[100, 500]}
          />
        </>
      )}

      <IncomeFormModal
        isOpen={isIncomeModalOpen}
        onClose={handleIncomeModalClose}
        editingIncome={incomeToEdit}
      />

      <DeleteDialog
        title="¿Estás seguro?"
        description="Esta acción no se puede deshacer. Se eliminará permanentemente el registro de ingreso."
        open={isDeleteIncomeDialogOpen}
        onOpenChange={setIsDeleteIncomeDialogOpen}
        onConfirm={confirmDeleteIncome}
      />

      <DeleteDialog
        title={`¿Eliminar ${selectedIncomes.length} ingreso(s)?`}
        description={`Esta acción no se puede deshacer. Se eliminarán permanentemente ${selectedIncomes.length} registro(s) de ingreso.`}
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        onConfirm={confirmBulkDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AdminIncomeTab;
